import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { Order, Orders } from '@services/order-services/models';
import { OrderActivity, ActivityResponse } from '@services/activity-services/models';
import { Query } from '@services/core-services/models';
import { PaginatedList } from '@services/core-services/models/paginated-list';
import { OrderListResponse } from '@services/order-services/models/order.response';
import { OrderDetailResponse } from '@services/order-services/models/order-detail.response';
import { OrderId } from '@services/order-services/models/order-id';
import { CancellationRequest } from './models/order-cancel';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { Invoices } from './models/invoice.modal';
import { OrderInvoiceResponse } from './models/invoice-response';
import { CheckInventoryResponse } from './models/inventory-response';
import { Inventory } from './models/inventory.modal';
import { Shipment } from './models/order-shipments';
import { OrderShipmentResponse } from './models/order-shipments.response';
import { CachingApiService } from '@services/core-services/api-cache.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  orderUrl = 'orders';
  constructor(
    private readonly cacheApi: CachingApiService,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  // getAllOrderData(query?: Query): Observable<PaginatedList<Orders[]>> {
  //   return this.cacheApi.get<OrderListResponse>(this.orderUrl, query).pipe(
  //     map(({ content }: OrderListResponse) => ({
  //       list: content.orders,
  //       pagination: content.pagination,
  //     }))
  //   );
  // }

  getAllOrderData(query?: Query): Observable<PaginatedList<Orders[]>> {
    return this.cacheApi.getWithCache<OrderListResponse>(this.orderUrl, query).pipe(
      map(({ content }: OrderListResponse) => ({
        list: content.orders,
        pagination: content.pagination,
      }))
    );
  }

  getOrderShipments(orderid: OrderId): Observable<Shipment> {
    const urlId = `${this.orderUrl}/${orderid}/shipments`;
    return this.cacheApi.getWithCache<OrderShipmentResponse>(urlId).pipe(
      map((response: OrderShipmentResponse) => {
        return response.content.shipment;
      })
    );
  }

  orderDetail(orderId: OrderId): Observable<Order> {
    const urlId = `${this.orderUrl}/${orderId}`;
    return this.cacheApi.getWithCache<OrderDetailResponse>(urlId).pipe(
      map((response: OrderDetailResponse) => {
        return response.content.order;
      })
    );
  }
  orderActivity(orderId: OrderId, query?: Query): Observable<PaginatedList<OrderActivity[]>> {
    const url = `${this.orderUrl}/${orderId}/activity`;
    return this.cacheApi.getWithCache<ActivityResponse>(url, query).pipe(
      map(({ content }: ActivityResponse) => ({
        list: content.activity,
        pagination: content.pagination,
      }))
    );
  }

  addOrderActivity(orderId: OrderId, activity: any): Observable<OrderActivity> {
    const url = `${this.orderUrl}/${orderId}/activity`;
    return this.cacheApi.post(url, activity).pipe(
      map((response: any) => {
        return response.content.activity;
      })
    );
  }

  cancelOrder(orderId: OrderId, Cancellation: CancellationRequest): Observable<Order> {
    const cancel = `${this.orderUrl}/${orderId}/cancel`;
    return this.cacheApi.post<CancellationRequest, OrderDetailResponse>(cancel, Cancellation).pipe(
      map(
        (response: OrderDetailResponse) => response.content.order,
        catchError(err => this.errorHandler.handleAndNotify(err, 'Failed to cancel Order'))
      )
    );
  }

  getOrderInvoiceDetail(orderId?: OrderId): Observable<Invoices[]> {
    const urlId = `${this.orderUrl}/${orderId}/invoice`;
    return this.cacheApi.getWithCache<OrderInvoiceResponse>(urlId).pipe(
      map((response: OrderInvoiceResponse) => {
        return response.content.invoices;
      })
    );
  }

  getCheckInventory(orderId?: OrderId): Observable<Inventory> {
    const urlId = `${this.orderUrl}/${orderId}/check-inventory`;
    return this.cacheApi.getWithCache<CheckInventoryResponse>(urlId).pipe(
      map((response: CheckInventoryResponse) => {
        return response.content;
      })
    );
  }
}
